import * as SurveyCore from "survey-core";
import SetContactMaxLengthAndValue from "./common/SetContactMaxLengthAndValue";
import SetLeadMaxLengthAndValue from "./common/SetLeadMaxLengthAndValue";
import i18next from "../../common/localization/i18n";
import FormStateParams from "../interfaces/FormStateParams";
import { SupportedCRM } from "../../common/enums/SupportedCRM";
import SetMapperAttributes from "../../common/helpers/SetMapperAttributes";
import { EntityType } from "../../common/enums/EntityType";
import UtmData from "../data/UtmData";
import SingleInputPropertiesHelper, {
  getDefaultUrlParameterName,
  isValidUrlParameterName,
} from "../functions/helpers/SingleInputPropertiesHelper";

const UTM_INPUT_TYPE = "utm";
const URL_PARAMETER_INPUT_TYPE = "urlparameter";
const URL_PARAMETER_NAME_VALIDATOR_FLAG = "__isUrlParameterNameValidator";

function isHiddenAutoFilledInputType(inputType: string) {
  return inputType === UTM_INPUT_TYPE || inputType === URL_PARAMETER_INPUT_TYPE;
}

function configureUrlParameterNameValidation() {
  const nameProperty = SurveyCore.Serializer.getProperty("question", "name");
  if (!nameProperty || nameProperty[URL_PARAMETER_NAME_VALIDATOR_FLAG]) {
    return;
  }

  const existingOnPropertyEditorUpdate = nameProperty.onPropertyEditorUpdate;

  nameProperty.onPropertyEditorUpdate = (obj: any, propEditor: any) => {
    existingOnPropertyEditorUpdate?.(obj, propEditor);

    if (!Array.isArray(propEditor?.validators)) {
      return;
    }

    propEditor.validators = propEditor.validators.filter(
      (validator: any) => !validator?.[URL_PARAMETER_NAME_VALIDATOR_FLAG],
    );

    if (obj?.getType?.() !== "text" || obj?.inputType !== URL_PARAMETER_INPUT_TYPE) {
      return;
    }

    const validator = new SurveyCore.RegexValidator("^clk_[A-Za-z0-9_-]+$");
    validator.text = i18next.t("NEW_FORM_EDITOR.SINGLE_INPUT.URL_PARAMETER.NAME_PREFIX_VALIDATION");
    (validator as any)[URL_PARAMETER_NAME_VALIDATOR_FLAG] = true;
    propEditor.validators.push(validator);
    propEditor.textUpdateMode = "onTyping";
  };

  nameProperty[URL_PARAMETER_NAME_VALIDATOR_FLAG] = true;
}

function SingleInput(formStateParams: FormStateParams) {
  configureUrlParameterNameValidation();

  SurveyCore.Serializer.addProperty("text", {
    name: "crmMaxLength",
    type: "number",
    default: 1,
    isSerializable: true,
    visible: false,
  });
  SurveyCore.Serializer.addProperty("text", {
    name: "crmContactMaxLength",
    type: "number",
    default: 1,
    isSerializable: true,
    visible: false,
  });
  SurveyCore.Serializer.addProperty("text", {
    name: "crmLeadMaxLength",
    type: "number",
    default: 1,
    isSerializable: true,
    visible: false,
  });

  SurveyCore.Serializer.getProperty("text", "inputType").onSetValue = (surveyElement, value) => {
    surveyElement.clearErrors();
    surveyElement.crmFieldType = value;
    surveyElement.inputMask = "none";
    surveyElement.inputType = value;

    if (value === URL_PARAMETER_INPUT_TYPE) {
      surveyElement.name = getDefaultUrlParameterName(
        surveyElement.name || surveyElement.title || "",
      );
      surveyElement.setPropertyValue("visible", false);
    }

    SingleInputPropertiesHelper(surveyElement);
  };

  const defaultValueProp = SurveyCore.Serializer.getProperty("text", "defaultValue");
  defaultValueProp.dependsOn = ["inputType"];
  defaultValueProp.visibleIf = (obj) => {
    return obj.inputType !== "email" && !isHiddenAutoFilledInputType(obj.inputType);
  };

  const showMaskOnHoverProp = SurveyCore.Serializer.getProperty("text", "showMaskOnHover");
  showMaskOnHoverProp.dependsOn = ["inputType"];
  showMaskOnHoverProp.visibleIf = (obj) => {
    return !isHiddenAutoFilledInputType(obj.inputType);
  };
  const visibleProp = SurveyCore.Serializer.getProperty("text", "visible");
  visibleProp.dependsOn = ["inputType"];
  visibleProp.visibleIf = (obj) => {
    return !isHiddenAutoFilledInputType(obj.inputType);
  };
  const isRequiredProp = SurveyCore.Serializer.getProperty("text", "isRequired");
  isRequiredProp.dependsOn = ["inputType"];
  isRequiredProp.visibleIf = (obj) => {
    return !isHiddenAutoFilledInputType(obj.inputType);
  };
  const readonlyProp = SurveyCore.Serializer.getProperty("text", "readOnly");
  readonlyProp.dependsOn = ["inputType"];
  readonlyProp.visibleIf = (obj) => {
    return !isHiddenAutoFilledInputType(obj.inputType);
  };

  const autoUnmaskProp = SurveyCore.Serializer.getProperty("text", "autoUnmask");
  autoUnmaskProp.dependsOn = ["inputType"];
  autoUnmaskProp.visibleIf = (obj) => {
    return !isHiddenAutoFilledInputType(obj.inputType);
  };

  SurveyCore.Serializer.addProperty("text", {
    name: "contactMap",
    displayName: i18next.t("NEW_FORM_EDITOR.COMPONENT_COMMON.CONTACT_MAP"),
    isRequired: false,
    dependsOn: ["inputType"],
    category: "toolboxMapperCategory",
    type: "dropdown",
    isSerializable: true,
    onSetValue: (obj: any, value: any) => {
      SetContactMaxLengthAndValue(obj, value, "contact", obj.inputType);
    },
    onPropertyEditorUpdate: (obj: any, editor: any) => {
      editor.allowClear = true;
    },
    visibleIf: (obj: any) => {
      return (
        obj.inputType === "text" ||
        obj.inputType === "email" ||
        obj.inputType === "url" ||
        obj.inputType === "integer" ||
        obj.inputType === "decimal" ||
        obj.inputType === "money"
      );
    },
    choices: (obj: any, choicesCallback: any) => {
      if (!choicesCallback) return;
      obj.clearErrors();
      if (obj.inputType === "text") {
        SetMapperAttributes("text", choicesCallback, formStateParams, EntityType.Contact, obj);
      }
      if (obj.inputType === "email") {
        SetMapperAttributes("email", choicesCallback, formStateParams, EntityType.Contact, obj);
      }
      if (obj.inputType === "url") {
        SetMapperAttributes("url", choicesCallback, formStateParams, EntityType.Contact, obj);
      }
      if (obj.inputType === "integer") {
        SetMapperAttributes("integer", choicesCallback, formStateParams, EntityType.Contact, obj);
      }
      if (obj.inputType === "decimal") {
        SetMapperAttributes("decimal", choicesCallback, formStateParams, EntityType.Contact, obj);
      }
      if (obj.inputType === "money") {
        SetMapperAttributes("money", choicesCallback, formStateParams, EntityType.Contact, obj);
      }
    },
    visibleIndex: 1,
  });

  SurveyCore.Serializer.addProperty("text", {
    name: "utmMap",
    displayName: i18next.t("NEW_FORM_EDITOR.SINGLE_INPUT.UTM.DROPDOWN_TITLE"),
    isRequired: false,
    dependsOn: ["inputType"],
    category: "toolboxMapperCategory",
    type: "dropdown",
    isSerializable: true,
    onPropertyEditorUpdate: (obj: any, editor: any) => {
      editor.allowClear = true;
    },
    visibleIf: (obj: any) => {
      return obj.inputType === UTM_INPUT_TYPE;
    },
    choices: Array.from(
      UtmData.map((c: { text: any; value: any }) => {
        return { text: c.text, value: c.value };
      }),
    ),
    visibleIndex: 1,
  });

  if (formStateParams.supportedCRM === SupportedCRM.MSCRM) {
    SurveyCore.Serializer.addProperty("text", {
      name: "leadMap",
      displayName: i18next.t("NEW_FORM_EDITOR.COMPONENT_COMMON.LEAD_MAP"),
      isRequired: false,
      dependsOn: ["inputType"],
      category: "toolboxMapperCategory",
      type: "dropdown",
      isSerializable: true,
      onSetValue: (obj: any, value: any) => {
        SetLeadMaxLengthAndValue(obj, value, "lead", obj.inputType);
      },
      onPropertyEditorUpdate: (obj: any, editor: any) => {
        editor.allowClear = true;
      },
      visibleIf: (obj: any) => {
        return (
          obj.inputType === "text" ||
          obj.inputType === "email" ||
          obj.inputType === "url" ||
          obj.inputType === "integer" ||
          obj.inputType === "decimal" ||
          obj.inputType === "money"
        );
      },
      choices: (obj: any, choicesCallback: any) => {
        if (!choicesCallback) return;
        obj.clearErrors();
        if (obj.inputType === "text") {
          SetMapperAttributes("text", choicesCallback, formStateParams, EntityType.Lead, obj);
        }
        if (obj.inputType === "email") {
          SetMapperAttributes("email", choicesCallback, formStateParams, EntityType.Lead, obj);
        }
        if (obj.inputType === "url") {
          SetMapperAttributes("url", choicesCallback, formStateParams, EntityType.Lead, obj);
        }
        if (obj.inputType === "integer") {
          SetMapperAttributes("integer", choicesCallback, formStateParams, EntityType.Lead, obj);
        }
        if (obj.inputType === "decimal") {
          SetMapperAttributes("decimal", choicesCallback, formStateParams, EntityType.Lead, obj);
        }
        if (obj.inputType === "money") {
          SetMapperAttributes("money", choicesCallback, formStateParams, EntityType.Lead, obj);
        }
      },
      visibleIndex: 1,
    });
  }

  SurveyCore.Serializer.addProperty("text", {
    name: "overrideInCrmMap",
    displayName: i18next.t("NEW_FORM_EDITOR.COMPONENT_COMMON.OVERRIDE_IN_CRM"),
    isRequired: false,
    category: "toolboxMapperCategory",
    type: "boolean",
    categoryIndex: 8,
    visibleIf: (obj: any) => {
      return (
        obj.inputType === "text" ||
        obj.inputType === "email" ||
        obj.inputType === "url" ||
        obj.inputType === "integer" ||
        obj.inputType === "decimal" ||
        obj.inputType === "money"
      );
    },
  });
}

export default SingleInput;
